import {
    Component,
    ElementRef,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';

import { DocumentService } from '@services/api/document/document.service';
import { SnackbarService } from '@services/snackbar.service';
import { TextButtonComponent } from '@shared/button/text-button/text-button.component';
import { FlatButtonComponent } from '@shared/button/flat-button/flat-button.component';
import { TooltipComponent } from '@shared/tooltip/tooltip.component';
import { LoaderComponent } from '@shared/loader/loader.component';
import { AutocompleteFixersComponent } from '@shared/autocomplete/autocomplete-fixers/autocomplete-fixers.component';
import { UnsavedChangesDialogComponent } from '@features/dialogs/unsaved-changes-dialog/unsaved-changes-dialog.component';

import { Collaborator } from '@models/document';
import { SelectOption } from '@models/shared.model';

import { SubscriptionList, SubscriptionListType } from '@helpers/subscription';
import DATA_QA from '@automation/data-qa.json';

const COLLABORATORS_BUTTON_TEXT = 'Collaborators';
const SUBSCRIPTION_KEY_LOAD_COLLABORATORS = 'load-collaborators';
const SUBSCRIPTION_KEY_SAVE_COLLABORATORS = 'save-collaborators';

@Component({
    selector: 'app-collaborators-collapsible',
    templateUrl: 'collaborators-collapsible.component.html',
    styleUrl: 'collaborators-collapsible.component.scss',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatIconModule,
        MatMenuModule,
        TextButtonComponent,
        FlatButtonComponent,
        TooltipComponent,
        LoaderComponent,
        AutocompleteFixersComponent,
    ],
})
export class CollaboratorsCollapsibleComponent implements OnChanges, OnDestroy {
    readonly dataQa = DATA_QA;

    @ViewChild('chipsContainer')
    chipsContainer?: ElementRef<HTMLElement>;

    @Input({ required: true })
    documentId!: number;

    @Input()
    documentName: string = '';

    @Input()
    mode: 'summary' | 'detail' = 'summary';

    @Input()
    readOnly = false;

    collaborators: Collaborator[] = [];
    candidateCollaborators: Collaborator[] = [];
    pendingCollaborators: Collaborator[] = [];
    candidateOptions: SelectOption[] = [];

    isLoading = false;
    menuOpened = false;
    isExpanded = false;
    isSaving = false;

    searchControl = new FormControl<string | SelectOption>('');

    private readonly documentService = inject(DocumentService);
    private readonly dialog = inject(MatDialog);
    private readonly snackbarService = inject(SnackbarService);
    private readonly _subscriptions =
        new SubscriptionList() as SubscriptionListType;

    private readonly removedExistingCollaboratorIds: Set<number> = new Set();
    private originalCollaboratorIds: Set<number> = new Set();

    get collaboratorsCount(): number {
        return this.collaborators.length;
    }

    get buttonText(): string {
        return COLLABORATORS_BUTTON_TEXT;
    }

    get isDetailMode(): boolean {
        return this.mode === 'detail';
    }

    get isEditableDetailMode(): boolean {
        return this.isDetailMode && !this.readOnly;
    }

    get panelTitle(): string {
        return this.collaboratorsCount > 0
            ? 'Manage Collaborators'
            : 'Invite Collaborators';
    }

    get hasUnsavedChanges(): boolean {
        const currentIds = new Set([
            ...this.collaborators.map((c) => c.id),
            ...this.pendingCollaborators.map((c) => c.id),
        ]);

        if (currentIds.size !== this.originalCollaboratorIds.size) {
            return true;
        }

        for (const id of currentIds) {
            if (!this.originalCollaboratorIds.has(id)) {
                return true;
            }
        }

        return false;
    }

    get isSaveDisabled(): boolean {
        return this.isSaving || !this.hasUnsavedChanges;
    }

    ngOnChanges(changes: SimpleChanges): void {
        const documentIdChange = changes['documentId'];
        if (documentIdChange?.currentValue) {
            this.loadCollaborators();
        }

        const readOnlyChange = changes['readOnly'];
        if (readOnlyChange?.currentValue === true && this.isExpanded) {
            this.isExpanded = false;
            this.pendingCollaborators = [];
            this.removedExistingCollaboratorIds.clear();
            this.clearSearch();
        }
    }

    ngOnDestroy(): void {
        this._subscriptions.unsubscribeAllSafe();
    }

    getCollaboratorDisplayText(collaborator: Collaborator): string {
        return `${collaborator.fullName} (${collaborator.email})`;
    }

    needsTooltip(collaborator: Collaborator): boolean {
        const displayText = this.getCollaboratorDisplayText(collaborator);
        const maxMenuWidth = 600;
        const paddingLeft = 20;
        const paddingRight = 12;
        const availableWidth = maxMenuWidth - paddingLeft - paddingRight;
        const estimatedWidth = displayText.length * 7;
        return estimatedWidth > availableWidth;
    }

    onButtonClick(event: Event): void {
        event.stopPropagation();

        if (this.isSaving) {
            return;
        }

        if (this.isEditableDetailMode) {
            this.togglePanel();
        }
    }

    onMenuOpened(): void {
        this.menuOpened = true;
    }

    onMenuClosed(): void {
        this.menuOpened = false;
    }

    togglePanel(): void {
        if (this.isExpanded) {
            this.cancelPanel();
        } else {
            this.openPanel();
        }
    }

    openPanel(): void {
        this.isExpanded = true;
        this.pendingCollaborators = [];
        this.removedExistingCollaboratorIds.clear();
        this.refreshCandidateOptions();
        this.clearSearch();
        this.originalCollaboratorIds = new Set(
            this.collaborators.map((c) => c.id),
        );
    }

    cancelPanel(): void {
        if (this.isSaving) {
            return;
        }

        if (this.hasUnsavedChanges) {
            this.showUnsavedChangesDialog();
        } else {
            this.closePanel();
        }
    }

    closePanel(): void {
        this.resetPanelState();
        this.loadCollaborators();
    }

    private resetPanelState(): void {
        this.isExpanded = false;
        this.pendingCollaborators = [];
        this.removedExistingCollaboratorIds.clear();
        this.refreshCandidateOptions();
        this.clearSearch();
    }

    onCandidateSelected(selection: { value: string | number }): void {
        if (this.isSaving) {
            return;
        }

        const selectedId = Number(selection.value);
        const collaborator = this.candidateCollaborators.find(
            (candidate) => candidate.id === selectedId,
        );

        if (!collaborator) {
            this.clearSearch();
            return;
        }

        if (this.isInactiveCollaborator(collaborator)) {
            this.clearSearch();
            return;
        }

        const existsInCurrent = this.collaborators.some(
            (item) => item.id === collaborator.id,
        );
        const existsInPending = this.pendingCollaborators.some(
            (item) => item.id === collaborator.id,
        );
        const wasRemovedFromExisting = this.removedExistingCollaboratorIds.has(
            collaborator.id,
        );

        if (!existsInCurrent && !existsInPending) {
            if (wasRemovedFromExisting) {
                this.collaborators = [...this.collaborators, collaborator];
                this.removedExistingCollaboratorIds.delete(collaborator.id);
            } else {
                this.pendingCollaborators = [
                    ...this.pendingCollaborators,
                    collaborator,
                ];
                this.scrollChipsToEnd();
            }
            this.refreshCandidateOptions();
        }

        this.clearSearch();
    }

    removePendingCollaborator(collaborator: Collaborator): void {
        if (this.isSaving) {
            return;
        }

        this.pendingCollaborators = this.pendingCollaborators.filter(
            (item) => item.id !== collaborator.id,
        );
        this.refreshCandidateOptions();
    }

    removeCollaborator(collaborator: Collaborator): void {
        if (this.isSaving) {
            return;
        }

        const isExistingCollaborator = this.originalCollaboratorIds.has(
            collaborator.id,
        );

        this.collaborators = this.collaborators.filter(
            (item) => item.id !== collaborator.id,
        );

        if (isExistingCollaborator) {
            this.removedExistingCollaboratorIds.add(collaborator.id);
        }

        const candidateExists = this.candidateCollaborators.some(
            (item) => item.id === collaborator.id,
        );

        if (!candidateExists) {
            this.candidateCollaborators = [
                ...this.candidateCollaborators,
                collaborator,
            ];
        }

        this.refreshCandidateOptions();
    }

    save(): void {
        if (this.isSaveDisabled) {
            return;
        }

        this.isSaving = true;

        const userIds = [
            ...new Set([
                ...this.collaborators.map((c) => c.id),
                ...this.pendingCollaborators.map((c) => c.id),
            ]),
        ];

        this._subscriptions[SUBSCRIPTION_KEY_SAVE_COLLABORATORS] =
            this.documentService
                .updateCollaborators(this.documentId, userIds)
                .subscribe({
                    next: (updatedCollaborators) => {
                        this.isSaving = false;

                        if (updatedCollaborators) {
                            this.collaborators = [...updatedCollaborators];
                        } else {
                            this.collaborators = [
                                ...this.collaborators,
                                ...this.pendingCollaborators,
                            ];
                        }

                        this.originalCollaboratorIds = new Set(
                            this.collaborators.map((c) => c.id),
                        );

                        this.snackbarService.success({
                            header: 'Collaborators updated successfully',
                        });
                        this.resetPanelState();
                    },
                    error: () => {
                        this.isSaving = false;
                        this.snackbarService.error({
                            header: 'Failed to update collaborators',
                        });
                    },
                });
    }

    clearSearch(): void {
        this.searchControl.setValue('');
    }

    private scrollChipsToEnd(): void {
        setTimeout(() => {
            const container = this.chipsContainer?.nativeElement;
            if (container) {
                container.scrollTop = container.scrollHeight;
            }
        });
    }

    private loadCollaborators(): void {
        if (!this.documentId) {
            return;
        }

        this.isLoading = true;

        this._subscriptions[SUBSCRIPTION_KEY_LOAD_COLLABORATORS] =
            this.documentService.getCollaborators(this.documentId).subscribe({
                next: ({ collaborators, candidates }) => {
                    this.collaborators = collaborators;
                    this.candidateCollaborators = candidates;
                    this.pendingCollaborators = [];
                    this.removedExistingCollaboratorIds.clear();
                    this.refreshCandidateOptions();
                    this.isLoading = false;
                },
                error: () => {
                    this.collaborators = [];
                    this.candidateCollaborators = [];
                    this.pendingCollaborators = [];
                    this.removedExistingCollaboratorIds.clear();
                    this.refreshCandidateOptions();
                    this.isLoading = false;
                },
            });
    }

    private refreshCandidateOptions(): void {
        const excludedIds = new Set([
            ...this.collaborators.map((c) => c.id),
            ...this.pendingCollaborators.map((c) => c.id),
        ]);

        this.candidateOptions = this.candidateCollaborators
            .filter((candidate) => !excludedIds.has(candidate.id))
            .map((candidate) => ({
                value: candidate.id,
                viewValue: this.getCollaboratorDisplayText(candidate),
                disabled: this.isInactiveCollaborator(candidate),
            }));
    }

    isInactiveCollaborator(collaborator: Collaborator): boolean {
        return collaborator.status !== null && collaborator.status !== 1;
    }

    private showUnsavedChangesDialog(): void {
        const unsavedDialogRef = this.dialog.open(
            UnsavedChangesDialogComponent,
            {
                data: {
                    unsavedChangeArea: 'Collaborators',
                    action: 'closing',
                },
                width: '480px',
            },
        );

        unsavedDialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }

            if (result.save) {
                this.save();
            } else if (result.leave) {
                this.closePanel();
            }
        });
    }
}
